import { HOST } from '../../utils/constants';
import { IEvent } from '../Couch/event';
import { IResults } from '../Couch/result';
import { ApiErrorResponse, ApiResponse } from '../Models/api';
import { HEADERS } from './headers';

interface ISentInviteEmailBody {
    athlete: string;
    event: string;
    division: string;
    ageCat?: string;
    type?: 'INVITE' | 'WAITLIST';
}

interface IWithdrawRequest {
    event: string;
    user: string;
    reason: string;
    refundRequested: boolean;
}

interface IWithdrawResponse {
    ok: true;
    id: string;
    rev: string;
}

export interface IAddPublicParti {
    eventId: string;
    partiId: string;
    fullname: string;
    classification: string;
    repersentingCountry: string;
    ageCat: string;
    division: string;
    status: 'Pending' | 'Approved' | 'PaymentPending' | 'InfoMissing';
    selectedGateway: string | undefined;
    totalAmount?: number;
    rest: any;
}

export interface IAddWaitlistParti {
    eventId: string;
    partiId: string;
    fullname: string;
    classification: string;
    repersentingCountry: string;
    ageCat: string;
    division: string;
}

export interface IUpdatePublicParti {
    eventId: string;
    partiId: string;
    fullname?: string;
    classification?: string;
    repersentingCountry?: string;
    ageCat?: string;
    division?: string;
    status?: string;
    competitorNumber?: number;
}

export interface ISimpleEvent {
    name: string;
    dates: string;
    overview: string;
    email: string;
    fee: string;
    type: string;
}

export interface ICouchDBResponse {
    ok: true;
    id: string;
    rev: string;
    metadata?: any;
}

export enum EEventTypes {
    SIMPLE,
    FULL
}

export const sendInviteEmail = async (data: ISentInviteEmailBody) => {
    const response = await fetch(HOST + 'email/invite', {
        ...HEADERS,
        body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response.json();
};

export const withdrawRequest = async (data: IWithdrawRequest): Promise<IWithdrawResponse> => {
    const response = await fetch(HOST + 'withdraw', {
        ...HEADERS,
        body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response.json();
};

export const addParticipant = async (data: IAddPublicParti): Promise<ICouchDBResponse> => {
    const response = await fetch(HOST + 'participant', {
        ...HEADERS,
        method: 'POST',
        body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response.json();
};

export const updateParticipant = async (data: IUpdatePublicParti): Promise<ICouchDBResponse> => {
    const response = await fetch(HOST + 'participant', {
        ...HEADERS,
        method: 'PUT',
        body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response.json();
};

// New methods in process to replace CouchDB with Express
export const getUpcomingEventsByClubId = async (clubId: string): Promise<ApiResponse<IEvent[]> | ApiErrorResponse> => {
    try {
        const response = await fetch(HOST + 'events/by-club/' + clubId, {
            ...HEADERS,
            method: 'GET',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        return error as ApiErrorResponse;
    }
};

// Get Club Events with Results
// TODO: Move it to results client
export const getClubEventsWithResults = async (clubId: string): Promise<ApiResponse<IResults[]> | ApiErrorResponse> => {
    try {
        const response = await fetch(HOST + 'events/with-results/' + clubId, {
            ...HEADERS,
            method: 'GET',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        return error as ApiErrorResponse;
    }
};

// Add Event
export const createEvent = async (data: IEvent): Promise<ApiResponse<IEvent> | ApiErrorResponse> => {
    try {
        const response = await fetch(HOST + 'event', {
            ...HEADERS,
            method: 'POST',
            body: JSON.stringify(data),
        });
        return await response.json();
    } catch (error) {
        return error as ApiErrorResponse;
    }
}

export const getEventDetails = async (event: string): Promise<ApiResponse<IEvent> | ApiErrorResponse> => {
    try {
        const response = await fetch(HOST + 'event/' + event, {
            ...HEADERS,
            method: 'GET',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        return error as ApiErrorResponse;
    }
};

// Get events by multiple ids
export const getEventsByIds = async (ids: string[]): Promise<ApiResponse<IEvent[]> | ApiErrorResponse> => {
    try {
        const response = await fetch(HOST + 'events/bulk', {
            ...HEADERS,
            method: 'POST',
            body: JSON.stringify(ids),
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        return error as ApiErrorResponse;
    }
};
